import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useFacility } from '../contexts/FacilityContext';
import { updateFacilityProfile, updateFacilityStatus, fetchPatients, fetchClaims } from '../services/api';
import { TYPE_COLORS } from '../data/facilities';
import {
  BILLING_TYPES,
  BILLING_TYPE_LABELS,
  BILLING_COMPONENTS,
  COMPONENT_LABELS,
  resolveFacilityComponent,
} from '../utils/billing';
import './FacilityHome.css';

const POS_LABELS = {
  '11': 'Office',
  '20': 'Urgent Care Facility',
  '21': 'Inpatient Hospital',
  '22': 'Outpatient Hospital',
  '49': 'Retail Clinic',
  '62': 'Comprehensive Outpatient Rehabilitation Facility',
};

const FACILITY_TYPES = ['Hospital', 'Clinic', 'Urgent Care', 'Rehabilitation', 'Specialty'];

function applyFieldMask(field, value) {
  switch (field) {
    case 'npi':
      return value.replace(/\D/g, '').slice(0, 10);
    case 'taxId': {
      const digits = value.replace(/\D/g, '').slice(0, 9);
      return digits.length > 2 ? `${digits.slice(0, 2)}-${digits.slice(2)}` : digits;
    }
    case 'phone':
    case 'fax': {
      const digits = value.replace(/\D/g, '').slice(0, 10);
      if (digits.length >= 7) return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
      if (digits.length >= 4) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
      if (digits.length >= 1) return `(${digits}`;
      return '';
    }
    case 'state':
      return value.replace(/[^a-zA-Z]/g, '').toUpperCase().slice(0, 2);
    case 'zip': {
      const digits = value.replace(/\D/g, '').slice(0, 9);
      return digits.length > 5 ? `${digits.slice(0, 5)}-${digits.slice(5)}` : digits;
    }
    case 'taxonomyCode':
      return value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase().slice(0, 10);
    default:
      return value;
  }
}

function FacilityHome() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { facilities, facilitiesLoading, selectFacility, updateFacility } = useFacility();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [statusAction, setStatusAction] = useState(null); // 'suspend' | 'reactivate' | 'cancel'
  const [statusLoading, setStatusLoading] = useState(false);
  const [statusError, setStatusError] = useState(null);
  const [suspendReason, setSuspendReason] = useState('');
  const [stats, setStats] = useState({ patientCount: 0, claimCount: 0 });

  const facility = facilities.find((f) => f.id === parseInt(id));

  useEffect(() => {
    if (facilitiesLoading) return;
    if (facility) {
      selectFacility(facility);
    } else {
      navigate('/', { replace: true });
    }
  }, [facility, facilitiesLoading, selectFacility, navigate]);

  useEffect(() => {
    if (!facility) return;
    Promise.all([
      fetchPatients(facility.id),
      fetchClaims(facility.id),
    ]).then(([patientsRes, claimsRes]) => {
      const patients = patientsRes.data ?? [];
      const claims = claimsRes.data ?? [];
      setStats({
        patientCount: Array.isArray(patients) ? patients.length : 0,
        claimCount: Array.isArray(claims) ? claims.length : 0,
      });
    }).catch(() => {});
  }, [facility?.id]);

  if (facilitiesLoading) return null;
  if (!facility) return null;

  const openEdit = () => {
    setForm({
      name:           facility.name,
      npi:            applyFieldMask('npi',   facility.npi   || ''),
      taxId:          applyFieldMask('taxId', facility.taxId || ''),
      placeOfService: facility.placeOfService,
      type:           facility.type,
      phone:          applyFieldMask('phone', facility.phone || ''),
      fax:            applyFieldMask('fax',   facility.fax   || ''),
      billingType:    facility.billingType,
      defaultBillingComponent: facility.defaultBillingComponent || '',
      address:        facility.address,
      city:           facility.city,
      state:          applyFieldMask('state',        facility.state        || ''),
      zip:            applyFieldMask('zip',           facility.zip          || ''),
      taxonomyCode:   applyFieldMask('taxonomyCode', facility.taxonomyCode || ''),
    });
    setEditing(true);
  };

  const handleFormChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: applyFieldMask(field, value) }));
  };

  const status = facility?.status || 'active';

  const handleStatusChange = async () => {
    const newStatus = statusAction === 'reactivate' ? 'active'
                    : statusAction === 'suspend'    ? 'suspended'
                    :                                 'cancelled';
    setStatusLoading(true);
    setStatusError(null);
    try {
      await updateFacilityStatus(facility.id, newStatus, statusAction === 'suspend' ? suspendReason : undefined);
      const update = { status: newStatus };
      if (statusAction === 'suspend') update.suspendReason = suspendReason;
      if (statusAction === 'reactivate') update.suspendReason = null;
      updateFacility(facility.id, update);
      setStatusAction(null);
      setSuspendReason('');
    } catch (err) {
      setStatusError(err.response?.data?.error || 'Failed to update facility status');
    } finally {
      setStatusLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setSaveError(null);
    try {
      const data = await updateFacilityProfile(facility.id, form);
      updateFacility(facility.id, data.facility);
      setEditing(false);
    } catch (err) {
      setSaveError(err.response?.data?.error || 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const { patientCount, claimCount } = stats;

  const profileFields = [
    { label: 'Facility NPI', value: facility.npi, icon: 'id' },
    { label: 'Tax ID (EIN)', value: facility.taxId, icon: 'hash' },
    { label: 'Place of Service', value: `${facility.placeOfService} — ${POS_LABELS[facility.placeOfService] || 'Other'}`, icon: 'map' },
    { label: 'Facility Type', value: facility.type, icon: 'tag' },
    { label: 'Phone', value: facility.phone, icon: 'phone' },
    { label: 'Fax', value: facility.fax, icon: 'fax' },
    { label: 'Billing Type', value: `${facility.billingType} Billing`, icon: 'billing' },
    {
      label: 'Billing Component',
      value: COMPONENT_LABELS[resolveFacilityComponent(facility)]
        + (facility.defaultBillingComponent ? '' : ' — from billing type'),
      icon: 'billing',
    },
    { label: 'Address', value: `${facility.address}, ${facility.city}, ${facility.state} ${facility.zip}`, icon: 'location' },
    { label: 'Taxonomy Code (FL 81)', value: facility.taxonomyCode || '—', icon: 'taxonomy' },
  ];

  const icons = {
    id: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="5" width="20" height="14" rx="2" />
        <line x1="2" y1="10" x2="22" y2="10" />
      </svg>
    ),
    hash: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="4" y1="9" x2="20" y2="9" /><line x1="4" y1="15" x2="20" y2="15" />
        <line x1="10" y1="3" x2="8" y2="21" /><line x1="16" y1="3" x2="14" y2="21" />
      </svg>
    ),
    map: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6" />
        <line x1="8" y1="2" x2="8" y2="18" /><line x1="16" y1="6" x2="16" y2="22" />
      </svg>
    ),
    tag: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
        <line x1="7" y1="7" x2="7.01" y2="7" />
      </svg>
    ),
    phone: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
      </svg>
    ),
    fax: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="6 9 6 2 18 2 18 9" />
        <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
        <rect x="6" y="14" width="12" height="8" />
      </svg>
    ),
    billing: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="3" width="20" height="18" rx="2" />
        <line x1="2" y1="9" x2="22" y2="9" />
        <line x1="9" y1="3" x2="9" y2="21" />
      </svg>
    ),
    location: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
        <circle cx="12" cy="10" r="3" />
      </svg>
    ),
    taxonomy: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 2H2v10l9.29 9.29a1 1 0 0 0 1.41 0l7.3-7.3a1 1 0 0 0 0-1.41L12 2z" />
        <path d="M7 7h.01" />
      </svg>
    ),
  };

  return (
    <div className="facility-home">
      {/* Breadcrumb */}
      <div className="fh-breadcrumb">
        <Link to="/" className="fh-breadcrumb-link">Facilities</Link>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="9 18 15 12 9 6" />
        </svg>
        <span className="fh-breadcrumb-current">{facility.name}</span>
      </div>

      {/* Hero Card */}
      <div className="fh-hero">
        <div className="fh-hero-top">
          <div className="fh-hero-info">
            <span
              className="fh-type-badge"
              style={{ backgroundColor: TYPE_COLORS[facility.type] || '#171363' }}
            >
              {facility.type}
            </span>
            <h1 className="fh-name">{facility.name}</h1>
            <p className="fh-address">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                <circle cx="12" cy="10" r="3" />
              </svg>
              {facility.address}, {facility.city}, {facility.state} {facility.zip}
            </p>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="fh-stats-row">
          <div className="fh-stat">
            <span className="fh-stat-value">{patientCount}</span>
            <span className="fh-stat-label">Patients</span>
          </div>
          <div className="fh-stat-divider" />
          <div className="fh-stat">
            <span className="fh-stat-value">{claimCount}</span>
            <span className="fh-stat-label">Claims</span>
          </div>
        </div>
      </div>

      {/* Profile Details */}
      <div className="fh-section">
        <div className="fh-section-header">
          <h2 className="fh-section-title">Facility Profile</h2>
          <button className="fh-edit-btn" onClick={openEdit} aria-label="Edit facility profile">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
            </svg>
            Edit
          </button>
        </div>
        <div className="fh-profile-grid">
          {profileFields.map((field) => (
            <div key={field.label} className="fh-profile-field">
              <div className="fh-field-icon">{icons[field.icon]}</div>
              <div className="fh-field-content">
                <span className="fh-field-label">{field.label}</span>
                <span className="fh-field-value">{field.value}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Facility Status */}
      <div className="fh-section">
        <div className="fh-section-header">
          <h2 className="fh-section-title">Facility Status</h2>
          <span className={`fh-status-badge fh-status-badge--${status}`}>
            {status.charAt(0).toUpperCase() + status.slice(1)}
          </span>
        </div>
        <div className="fh-status-body">
          {status === 'active' && (
            <>
              <p className="fh-status-desc">This facility is active and accepting claims.</p>
              <button className="fh-btn-status fh-btn-suspend" onClick={() => setStatusAction('suspend')}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="6" y="4" width="4" height="16" /><rect x="14" y="4" width="4" height="16" />
                </svg>
                Suspend Facility
              </button>
            </>
          )}
          {status === 'suspended' && (
            <>
              <p className="fh-status-desc">This facility is suspended. Claims cannot be submitted until reactivated.</p>
              {facility.suspendReason && (
                <div className="fh-suspend-reason-display">
                  <span className="fh-suspend-reason-label">Reason:</span>
                  <span className="fh-suspend-reason-text">{facility.suspendReason}</span>
                </div>
              )}
              <div className="fh-status-actions">
                <button className="fh-btn-status fh-btn-reactivate" onClick={() => setStatusAction('reactivate')}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="23 4 23 10 17 10" /><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
                  </svg>
                  Reactivate
                </button>
                <button className="fh-btn-status fh-btn-cancel-facility" onClick={() => setStatusAction('cancel')}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" />
                  </svg>
                  Cancel Facility
                </button>
              </div>
            </>
          )}
          {status === 'cancelled' && (
            <p className="fh-status-desc">This facility has been permanently cancelled. No further actions are available.</p>
          )}
        </div>
      </div>

      {/* Status Confirmation Modal */}
      {statusAction && (
        <div className="fh-modal-backdrop">
          <div className="fh-modal">
            <div className="fh-modal-header">
              <div className="fh-modal-header-left">
                <div className="fh-modal-icon" style={{
                  backgroundColor: statusAction === 'cancel' ? '#fee2e2'
                                 : statusAction === 'suspend' ? '#fef3c7'
                                 : '#dcfce7',
                  color: statusAction === 'cancel' ? '#dc2626'
                       : statusAction === 'suspend' ? '#d97706'
                       : '#16a34a',
                }}>
                  {statusAction === 'cancel' && (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" />
                    </svg>
                  )}
                  {statusAction === 'suspend' && (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="6" y="4" width="4" height="16" /><rect x="14" y="4" width="4" height="16" />
                    </svg>
                  )}
                  {statusAction === 'reactivate' && (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="23 4 23 10 17 10" /><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
                    </svg>
                  )}
                </div>
                <div>
                  <h2 className="fh-modal-title">
                    {statusAction === 'suspend'    ? 'Suspend Facility'
                   : statusAction === 'reactivate' ? 'Reactivate Facility'
                   :                                 'Cancel Facility'}
                  </h2>
                  <p className="fh-modal-subtitle">{facility.name}</p>
                </div>
              </div>
              <button className="fh-modal-close" onClick={() => { setStatusAction(null); setStatusError(null); setSuspendReason(''); }} aria-label="Close">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            <div className="fh-confirm-body">
              {statusAction === 'suspend' && (
                <>
                  <p className="fh-status-desc">Suspending this facility will pause all claim submissions. You can reactivate it at any time.</p>
                  <div className="fh-form-field">
                    <label htmlFor="suspend-reason">Reason for Suspension</label>
                    <textarea
                      id="suspend-reason"
                      className="fh-suspend-reason-input"
                      value={suspendReason}
                      onChange={(e) => setSuspendReason(e.target.value)}
                      placeholder="Enter reason for suspending this facility..."
                      rows={3}
                      maxLength={500}
                    />
                  </div>
                </>
              )}
              {statusAction === 'reactivate' && (
                <p className="fh-status-desc">Reactivating this facility will resume normal claim submissions.</p>
              )}
              {statusAction === 'cancel' && (
                <p className="fh-confirm-warning">
                  This action is <strong>permanent and cannot be undone.</strong> The facility will be cancelled and no further claims can be submitted.
                </p>
              )}
              {statusError && <p className="fh-form-error" style={{ marginTop: 12 }}>{statusError}</p>}
            </div>

            <div className="fh-modal-footer">
              <button type="button" className="fh-btn-cancel" onClick={() => { setStatusAction(null); setStatusError(null); setSuspendReason(''); }}>
                Back
              </button>
              <button
                type="button"
                className={`fh-btn-status-confirm fh-btn-status-confirm--${statusAction}`}
                onClick={handleStatusChange}
                disabled={statusLoading}
              >
                {statusLoading && <span className="fh-spinner" />}
                {statusAction === 'suspend'    ? 'Suspend'
               : statusAction === 'reactivate' ? 'Reactivate'
               :                                 'Cancel Facility'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editing && (
        <div className="fh-modal-backdrop">
          <div className="fh-modal">
            <div className="fh-modal-header">
              <div className="fh-modal-header-left">
                <div className="fh-modal-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
                  </svg>
                </div>
                <div>
                  <h2 className="fh-modal-title">Edit Facility Profile</h2>
                  <p className="fh-modal-subtitle">Update details for {facility.name}</p>
                </div>
              </div>
              <button className="fh-modal-close" onClick={() => { setEditing(false); setSaveError(null); }} aria-label="Close">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleSave} className="fh-edit-form">
              <div className="fh-form-section">
                <span className="fh-form-section-label">General</span>
                <div className="fh-form-grid">
                  <div className="fh-form-field fh-form-full">
                    <label htmlFor="edit-name">Facility Name</label>
                    <input id="edit-name" type="text" value={form.name} onChange={(e) => handleFormChange('name', e.target.value)} maxLength={100} required />
                  </div>
                  <div className="fh-form-field">
                    <label htmlFor="edit-npi">Facility NPI</label>
                    <input id="edit-npi" type="text" value={form.npi} onChange={(e) => handleFormChange('npi', e.target.value)} maxLength={10} inputMode="numeric" required />
                  </div>
                  <div className="fh-form-field">
                    <label htmlFor="edit-taxId">Tax ID (EIN)</label>
                    <input id="edit-taxId" type="text" value={form.taxId} onChange={(e) => handleFormChange('taxId', e.target.value)} maxLength={10} inputMode="numeric" required />
                  </div>
                  <div className="fh-form-field">
                    <label htmlFor="edit-type">Facility Type</label>
                    <select id="edit-type" value={form.type} onChange={(e) => handleFormChange('type', e.target.value)}>
                      {FACILITY_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div className="fh-form-field">
                    <label htmlFor="edit-pos">Place of Service</label>
                    <select id="edit-pos" value={form.placeOfService} onChange={(e) => handleFormChange('placeOfService', e.target.value)}>
                      {Object.entries(POS_LABELS).map(([code, label]) => (
                        <option key={code} value={code}>{code} — {label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="fh-form-field">
                    <label htmlFor="edit-billing">Billing Type</label>
                    <select id="edit-billing" value={form.billingType} onChange={(e) => handleFormChange('billingType', e.target.value)}>
                      {BILLING_TYPES.map((b) => <option key={b} value={b}>{BILLING_TYPE_LABELS[b]}</option>)}
                    </select>
                  </div>
                  {/* The radiology billing component, which is a different question from
                      Billing Type above: that one picks the FORM (and its 'Global' means a
                      UB-04), this one decides whether a claim bills TC, 26 or both. Left
                      unset it follows the billing type, which is what every facility did
                      before the column existed. */}
                  <div className="fh-form-field">
                    <label htmlFor="edit-component">Billing Component</label>
                    <select id="edit-component" value={form.defaultBillingComponent} onChange={(e) => handleFormChange('defaultBillingComponent', e.target.value)}>
                      <option value="">Follow billing type ({COMPONENT_LABELS[resolveFacilityComponent({ billingType: form.billingType })]})</option>
                      {BILLING_COMPONENTS.map((c) => <option key={c} value={c}>{COMPONENT_LABELS[c]}</option>)}
                    </select>
                  </div>
                  <div className="fh-form-field">
                    <label htmlFor="edit-taxonomy">Taxonomy Code <span style={{ fontWeight: 400, color: '#6b7280', fontSize: '0.8em' }}>FL 81</span></label>
                    <input id="edit-taxonomy" type="text" value={form.taxonomyCode} onChange={(e) => handleFormChange('taxonomyCode', e.target.value)} maxLength={10} placeholder="e.g. 282N00000X" />
                  </div>
                </div>
              </div>

              <div className="fh-form-section">
                <span className="fh-form-section-label">Contact</span>
                <div className="fh-form-grid">
                  <div className="fh-form-field">
                    <label htmlFor="edit-phone">Phone</label>
                    <input id="edit-phone" type="tel" value={form.phone} onChange={(e) => handleFormChange('phone', e.target.value)} maxLength={14} placeholder="(XXX) XXX-XXXX" />
                  </div>
                  <div className="fh-form-field">
                    <label htmlFor="edit-fax">Fax</label>
                    <input id="edit-fax" type="tel" value={form.fax} onChange={(e) => handleFormChange('fax', e.target.value)} maxLength={14} placeholder="(XXX) XXX-XXXX" />
                  </div>
                </div>
              </div>

              <div className="fh-form-section">
                <span className="fh-form-section-label">Address</span>
                <div className="fh-form-grid">
                  <div className="fh-form-field fh-form-full">
                    <label htmlFor="edit-address">Street Address</label>
                    <input id="edit-address" type="text" value={form.address} onChange={(e) => handleFormChange('address', e.target.value)} maxLength={150} required />
                  </div>
                  <div className="fh-form-field">
                    <label htmlFor="edit-city">City</label>
                    <input id="edit-city" type="text" value={form.city} onChange={(e) => handleFormChange('city', e.target.value)} maxLength={50} required />
                  </div>
                  <div className="fh-form-field fh-form-half">
                    <label htmlFor="edit-state">State</label>
                    <input id="edit-state" type="text" value={form.state} onChange={(e) => handleFormChange('state', e.target.value)} maxLength={2} placeholder="TX" required />
                  </div>
                  <div className="fh-form-field fh-form-half">
                    <label htmlFor="edit-zip">Zip</label>
                    <input id="edit-zip" type="text" value={form.zip} onChange={(e) => handleFormChange('zip', e.target.value)} maxLength={10} inputMode="numeric" required />
                  </div>
                </div>
              </div>

              {saveError && <p className="fh-form-error" style={{ margin: '0 0 8px' }}>{saveError}</p>}
              <div className="fh-modal-footer">
                <button type="button" className="fh-btn-cancel" onClick={() => { setEditing(false); setSaveError(null); }}>Cancel</button>
                <button type="submit" className="fh-btn-save" disabled={saving}>
                  {saving && <span className="fh-spinner" />}
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default FacilityHome;
